import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { Check, LogOut, Plus, Trash2, User, UserCircle } from "@fleet/icons";
import { cn, type StoredAccount } from "@listto/shared";
import { Avatar } from "@/components/ui/avatar";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { useAccounts } from "./use-accounts";

interface AccountSwitcherProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sign out of the active account (delegates to the boot-manager sign-out). */
  onSignOut: () => void;
  /** Clear the GUEST's local database (shown instead of sign-out on a guest tab). */
  onClearData: () => void;
}

/**
 * Account switcher overlay (Gmail / Microsoft To Do style). Lists every known
 * account (active highlighted), plus "Add account", "Continue as guest" and a
 * per-account remove action. Opened URL-addressably via `?account=switch`.
 */
export function AccountSwitcher(props: AccountSwitcherProps) {
  const { t } = useI18n();
  const session = useSession();
  const { accounts, activeUserId, switchTo, addAccount, continueAsGuest, removeAccount } =
    useAccounts();

  const close = () => {
    props.onOpenChange(false);
  };

  const handleSwitch = (account: StoredAccount) => {
    close();
    if (account.userId === activeUserId()) {
      return;
    }
    void switchTo(account.userId);
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader>
        <DialogTitle class="text-sm font-bold uppercase tracking-widest opacity-50 pl-2">
          {t(msg`Accounts`)}
        </DialogTitle>
      </DialogHeader>

      <div class="p-2 space-y-1 max-h-[50vh] overflow-y-auto scrollbar-hide">
        <For each={accounts()}>
          {(account) => {
            const isActive = () => account.userId === activeUserId();
            return (
              <div
                class={cn(
                  "w-full flex items-center gap-3 p-3 rounded-2xl transition-colors group",
                  isActive() ? "bg-accent/10" : "hover:bg-accent/5",
                )}
              >
                <button
                  type="button"
                  onClick={() => {
                    handleSwitch(account);
                  }}
                  class="flex flex-1 items-center gap-3 text-left cursor-pointer min-w-0"
                >
                  <Show
                    when={account.kind !== "guest"}
                    fallback={
                      <div class="h-10 w-10 shrink-0 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                        <UserCircle class="h-5 w-5" />
                      </div>
                    }
                  >
                    <Avatar
                      userId={account.userId}
                      name={account.displayName}
                      email={account.email}
                      image={account.avatar}
                      size="md"
                      testId="account-switcher-avatar"
                    />
                  </Show>
                  <div class="min-w-0">
                    <div class="font-bold truncate">
                      {account.kind === "guest" ? t(msg`Guest`) : account.displayName}
                    </div>
                    <Show when={account.email}>
                      <div class="text-xs text-muted-foreground truncate">{account.email}</div>
                    </Show>
                  </div>
                </button>

                <Show when={isActive()}>
                  <Check class="h-4 w-4 shrink-0 text-accent" aria-label={t(msg`Active`)} />
                </Show>

                <Show when={account.kind === "real"}>
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      void removeAccount(account);
                    }}
                    class="shrink-0 rounded-md p-1.5 text-muted-foreground/70 opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                    aria-label={t(msg`Remove account`)}
                    title={t(msg`Remove account`)}
                  >
                    <LogOut class="h-4 w-4" />
                  </button>
                </Show>
              </div>
            );
          }}
        </For>
      </div>

      <div class="p-2 space-y-1 border-t border-border/40">
        <button
          type="button"
          onClick={() => {
            // No `close()`: `addAccount` navigates to `/login` (outside the app
            // tree), which closes the overlay without racing the `.`
            // close-navigation against it.
            void addAccount();
          }}
          class="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-accent/5 transition-colors text-left cursor-pointer"
          data-testid="add-account"
        >
          <div class="h-10 w-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary">
            <Plus class="h-5 w-5" />
          </div>
          <div class="font-bold">{t(msg`Add account`)}</div>
        </button>

        <button
          type="button"
          onClick={() => {
            // No `close()`: `continueAsGuest` navigates to `/` (no search),
            // which drops `?account=switch` and closes the overlay.
            void continueAsGuest();
          }}
          class="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-accent/5 transition-colors text-left cursor-pointer"
          data-testid="continue-as-guest"
        >
          <div class="h-10 w-10 shrink-0 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
            <User class="h-5 w-5" />
          </div>
          <div class="font-bold">{t(msg`Continue as guest`)}</div>
        </button>
      </div>

      <div class="p-4 bg-muted/20">
        {/* A GUEST tab has no session to sign out of — offer "clear local data"
            instead (wipes the guest's local DB). Real account → sign out. */}
        <Show
          when={session.isVisitor()}
          fallback={
            <Button
              variant="ghost"
              class="w-full h-10 font-bold rounded-xl text-destructive hover:text-destructive"
              onClick={() => {
                close();
                props.onSignOut();
              }}
            >
              <LogOut class="mr-2 h-4 w-4" />
              {t(msg`Sign out of current account`)}
            </Button>
          }
        >
          <Button
            variant="ghost"
            class="w-full h-10 font-bold rounded-xl text-destructive hover:text-destructive"
            onClick={() => {
              close();
              props.onClearData();
            }}
          >
            <Trash2 class="mr-2 h-4 w-4" />
            {t(msg`Clear local data`)}
          </Button>
        </Show>
      </div>
    </Dialog>
  );
}
